/**
 * Page Registry
 * React.lazy() map for all page components, enabling code-splitting by route.
 */

import { lazy } from 'react';

// Auth pages
export const LoginPage = lazy(() => import('../pages/LoginPage'));
export const ForgotPasswordPage = lazy(() => import('../pages/ForgotPasswordPage').then(m => ({ default: m.ForgotPasswordPage })));
export const ActivationPage = lazy(() => import('../pages/ActivationPage'));

// Dashboard
export const DashboardPage = lazy(() => import('../pages/DashboardPage'));

// Policies
export const PoliciesListPage = lazy(() => import('../pages/PoliciesListPage'));
export const PolicyDetailPage = lazy(() => import('../pages/PolicyDetailPage'));
export const CreatePolicyPage = lazy(() => import('../pages/CreatePolicyPage'));
export const EditPolicyPage = lazy(() => import('../pages/EditPolicyPage'));

// Buildings
export const BuildingsListPage = lazy(() => import('../pages/BuildingsListPage'));
export const BuildingDetailPage = lazy(() => import('../pages/BuildingDetailPage'));
export const BuildingEditorPage = lazy(() => import('../pages/BuildingEditorPage'));

// Findings
export const FindingsListPage = lazy(() => import('../pages/FindingsListPage'));
export const FindingDetailPage = lazy(() => import('../pages/FindingDetailPage'));
export const CreateFindingPage = lazy(() => import('../pages/CreateFindingPage'));
export const EditFindingPage = lazy(() => import('../pages/EditFindingPage'));

// Photos
export const PhotoSearchPage = lazy(() => import('../pages/PhotoSearchPage'));
export const PhotoDetailPage = lazy(() => import('../pages/PhotoDetailPage'));
export const PhotoEditPage = lazy(() => import('../pages/PhotoEditPage'));
export const PhotoUploadPage = lazy(() => import('../pages/PhotoUploadPage'));

// Inspections
export const InspectionsListPage = lazy(() => import('../pages/InspectionsListPage'));
export const InspectionDetailPage = lazy(() => import('../pages/InspectionDetailPage'));
export const InspectionJobsPage = lazy(() => import('../pages/InspectionJobsPage'));
export const InspectorDashboardPage = lazy(() => import('../pages/InspectorDashboardPage'));

// Subjectivities
export const SubjectivitiesListPage = lazy(() => import('../pages/SubjectivitiesListPage'));
export const SubjectivityDetailPage = lazy(() => import('../pages/SubjectivityDetailPage'));
export const CreateSubjectivityPage = lazy(() => import('../pages/CreateSubjectivityPage'));

// Risk & Exceptions
export const ExceptionQueuePage = lazy(() => import('../pages/ExceptionQueuePage'));
export const VARPage = lazy(() => import('../pages/VARPage'));
export const SecureLinksPage = lazy(() => import('../pages/SecureLinksPage'));
export const VARSharedViewPage = lazy(() => import('../pages/VARSharedViewPage'));

// Documents
export const DocumentSearchPage = lazy(() => import('../pages/DocumentSearchPage'));

// Analytics
export const PortfolioAnalyticsPage = lazy(() => import('../pages/PortfolioAnalyticsPage'));

// Broker
export const RenewalTrackerPage = lazy(() => import('../pages/RenewalTrackerPage'));
export const RenewalDetailPage = lazy(() => import('../pages/RenewalDetailPage'));
export const StructureDetailPage = lazy(() => import('../pages/StructureDetailPage'));
export const EvidencePacketPage = lazy(() => import('../pages/EvidencePacketPage'));
export const ImportPortfolioPage = lazy(() => import('../pages/ImportPortfolioPage'));

// Underwriter
export const UnderwriterReviewPage = lazy(() => import('../pages/UnderwriterReviewPage'));

// Broker Report (cloned from Underwriter/Evidence workflow)
export const BrokerReviewPage = lazy(() => import('../pages/BrokerReviewPage'));
export const BrokerReportDetailPage = lazy(() => import('../pages/BrokerReportDetailPage'));
export const BrokerEvidencePacketPage = lazy(() => import('../pages/BrokerEvidencePacketPage'));

// Mobile
export const MobileUploadPage = lazy(() => import('../pages/MobileUploadPage'));

// AI Analysis (Preview)
export const AIAnalysisPage = lazy(() => import('../pages/AIAnalysisPage'));

// Admin Console
export const AdminIntegrationsPage = lazy(() => import('../pages/AdminIntegrationsPage'));
export const AdminUsersPage = lazy(() => import('../pages/AdminUsersPage'));
export const AdminUserDetailPage = lazy(() => import('../pages/AdminUserDetailPage'));
export const AdminRolesPage = lazy(() => import('../pages/AdminRolesPage'));
export const AdminPartnersPage = lazy(() => import('../pages/AdminPartnersPage'));
export const AdminHealthPage = lazy(() => import('../pages/AdminHealthPage'));
export const AdminLogsPage = lazy(() => import('../pages/AdminLogsPage'));
export const AdminKPIPage = lazy(() => import('../pages/AdminKPIPage'));
export const AdminAlertsPage = lazy(() => import('../pages/AdminAlertsPage'));
export const AdminIndexPage = lazy(() => import('../pages/AdminIndexPage'));
export const AdminBackfillThumbnailsPage = lazy(() => import('../pages/AdminBackfillThumbnailsPage'));
export const AdminFlagsPage = lazy(() => import('../pages/AdminFlagsPage'));

// Navigation & Settings
export const MoreMenuPage = lazy(() => import('../pages/MoreMenuPage'));
export const UserSettingsPage = lazy(() => import('../pages/UserSettingsPage'));
export const NotificationSettingsPage = lazy(() => import('../pages/NotificationSettingsPage'));

// Gated Viral Identity Layer
export const SignupPage = lazy(() => import('../pages/SignupPage'));
export const AdminPartnerDomainsPage = lazy(() => import('../pages/AdminPartnerDomainsPage'));
export const AdminPendingInvitesPage = lazy(() => import('../pages/AdminPendingInvitesPage'));

// Claims / FNOL
export const ClaimsListPage = lazy(() => import('../pages/ClaimsListPage'));
export const ClaimDetailPage = lazy(() => import('../pages/ClaimDetailPage'));

// Legal / Compliance (public, no auth required)
export const TermsPage = lazy(() => import('../pages/TermsPage'));
export const PrivacyPage = lazy(() => import('../pages/PrivacyPage'));
export const SecurityPage = lazy(() => import('../pages/SecurityPage'));

